import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEOHead } from '../components/SEO';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      <SEOHead
        title="Privacy Policy | The Hotel Emerald"
        description="Privacy Policy for The Hotel Emerald. Learn how we collect, use, and protect your personal information."
      />
      <Navbar />

      {/* Hero */}
      <section className="bg-emerald-950 py-20">
        <div className="container-custom text-center text-white">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-serif text-4xl md:text-5xl mb-4"
          >
            Privacy Policy
          </motion.h1>
          <p className="text-emerald-200">Last updated: February 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">1. Information We Collect</h2>
              <p className="text-muted-foreground mb-4">
                At The Hotel Emerald, we collect information to provide you with exceptional hospitality services. This includes:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Personal Information:</strong> Name, email address, phone number, and postal address when you make a reservation or inquiry.</li>
                <li><strong>Booking Information:</strong> Check-in/check-out dates, room preferences, special requests, and payment details.</li>
                <li><strong>Communication Data:</strong> Records of correspondence when you contact us via email, phone, or contact forms.</li>
                <li><strong>Website Usage:</strong> IP address, browser type, pages visited, and cookies for improving your experience.</li>
              </ul>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">2. How We Use Your Information</h2>
              <p className="text-muted-foreground mb-4">We use your information to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Process and confirm your reservations</li>
                <li>Communicate booking details and updates</li>
                <li>Respond to inquiries and provide customer support</li>
                <li>Send promotional offers and newsletters (with your consent)</li>
                <li>Improve our services and website experience</li>
                <li>Comply with legal obligations</li>
              </ul>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">3. Information Sharing</h2>
              <p className="text-muted-foreground mb-4">
                We do not sell your personal information. We may share data with:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Payment Processors:</strong> To securely process your transactions</li>
                <li><strong>Booking Partners:</strong> When reservations are made through third-party platforms</li>
                <li><strong>Service Providers:</strong> Who assist us in operating our business</li>
                <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
              </ul>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">4. Data Security</h2>
              <p className="text-muted-foreground mb-4">
                We implement industry-standard security measures to protect your information, including:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>SSL encryption for data transmission</li>
                <li>Secure payment processing through trusted providers</li>
                <li>Regular security audits and updates</li>
                <li>Restricted access to personal data on a need-to-know basis</li>
              </ul>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">5. Cookies</h2>
              <p className="text-muted-foreground mb-4">
                Our website uses cookies to enhance your browsing experience. These include:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Essential Cookies:</strong> Required for website functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                <li><strong>Preference Cookies:</strong> Remember your settings (e.g., dark mode)</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                You can manage cookie preferences through your browser settings.
              </p>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">6. Your Rights</h2>
              <p className="text-muted-foreground mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data (subject to legal requirements)</li>
                <li>Opt-out of marketing communications</li>
                <li>Lodge a complaint with a data protection authority</li>
              </ul>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">7. Data Retention</h2>
              <p className="text-muted-foreground mb-4">
                We retain your information only as long as necessary to provide our services and comply with legal obligations. Booking records are typically retained for 7 years for accounting purposes.
              </p>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">8. Contact Us</h2>
              <p className="text-muted-foreground mb-4">
                For privacy-related inquiries or to exercise your rights, contact us at:
              </p>
              <div className="bg-muted p-6 rounded-lg">
                <p className="text-foreground font-medium">The Hotel Emerald</p>
                <p className="text-muted-foreground">NALCO Chowk, Semiliguda</p>
                <p className="text-muted-foreground">Koraput, Odisha 764036, India</p>
                <p className="text-muted-foreground">Email: thehotelemerald@gmail.com</p>
                <p className="text-muted-foreground">Phone: +91 6372 350 065</p>
              </div>

              <h2 className="font-serif text-2xl text-foreground mt-8 mb-4">9. Policy Updates</h2>
              <p className="text-muted-foreground mb-4">
                We may update this Privacy Policy periodically. Changes will be posted on this page with an updated revision date. Continued use of our services constitutes acceptance of any modifications.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
